import { Injectable } from "@angular/core";
import API, { graphqlOperation } from "@aws-amplify/api-graphql";
import { CreateTemplateInput, ListTemplatesByClientIDProductsEnabledPKQuery, ModelSortDirection, ModelStringKeyConditionInput, UpdateTemplateInput } from "../../../API.service";
import { TranslateService } from "@ngx-translate/core";
import { BANK_STATEMENT_FIELD_LABELS, isBankStatementField } from "../../constants/bank-statement-fields";

export enum TemplateType {
  REGULAR = 'regular',
  GOODS = 'goods'
}

export type ModelSizeInput = {
  ne?: number | null;
  eq?: number | null;
  le?: number | null;
  lt?: number | null;
  ge?: number | null;
  gt?: number | null;
  between?: Array<number | null> | null;
};

export type CreateTemplateMutation = {
  __typename: "Template";
  id: string;
  _id?: string | null;
  _name?: string | null;
  _company?: string | null;
  _date?: string | null;
  _pib?: string | null;
  isFixedValueEnabled?: string | null;
  fixedValues?: string | null;
  createdAt: string;
  updatedAt: string;
};

export enum ModelAttributeTypes {
  binary = "binary",
  binarySet = "binarySet",
  bool = "bool",
  list = "list",
  map = "map",
  number = "number",
  numberSet = "numberSet",
  string = "string",
  stringSet = "stringSet",
  _null = "_null"
}

export type ModelStringInput = {
  ne?: string | null;
  eq?: string | null;
  le?: string | null;
  lt?: string | null;
  ge?: string | null;
  gt?: string | null;
  contains?: string | null;
  notContains?: string | null;
  between?: Array<string | null> | null;
  beginsWith?: string | null;
  attributeExists?: boolean | null;
  attributeType?: ModelAttributeTypes | null;
  size?: ModelSizeInput | null;
};

export type ListTemplatesQuery = {
  __typename: "ModelTemplateConnection";
  items: Array<{
    __typename: "Template";
    id: string;
    _id?: string | null;
    _name?: string | null;
    _description?: string | null;
    _document?: string | null;
    _company?: string | null;
    _date?: string | null;
    _invoice_date?: string | null;
    _due_date?: string | null;
    _price?: string | null;
    _vat?: string | null;
    _total?: string | null;
    _invoice_number?: string | null;
    _pib?: string | null;
    isFixedValueEnabled?: string | null;
    fixedValues?: string | null;
    createdAt: string;
    updatedAt: string;
  } | null>;
  nextToken?: string | null;
};


export type ModelIDInput = {
  ne?: string | null;
  eq?: string | null;
  le?: string | null;
  lt?: string | null;
  ge?: string | null;
  gt?: string | null;
  contains?: string | null;
  notContains?: string | null;
  between?: Array<string | null> | null;
  beginsWith?: string | null;
  attributeExists?: boolean | null;
  attributeType?: ModelAttributeTypes | null;
  size?: ModelSizeInput | null;
};

export type ModelTemplateFilterInput = {
  id?: ModelIDInput | null;
  _id?: ModelStringInput | null;
  _name?: ModelStringInput | null;
  clientID?: ModelIDInput | null;
  searchKeyword?: ModelStringInput | null;
  _company?: ModelStringInput | null;
  _date?: ModelStringInput | null;
  _pib?: ModelStringInput | null;
  and?: Array<ModelTemplateFilterInput | null> | null;
  or?: Array<ModelTemplateFilterInput | null> | null;
  not?: ModelTemplateFilterInput | null;
};

export type DeleteTemplateInput = {
  id: string;
};

export type DeleteTemplateMutation = {
  __typename: "Template";
  id: string;
  _id?: string | null;
  _name?: string | null;
  _company?: string | null;
  _date?: string | null;
  _pib?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UpdateTemplateMutation = {
  __typename: "Template";
  id: string;
  _id?: string | null;
  _name?: string | null;
  _company?: string | null;
  _date?: string | null;
  _pib?: string | null;
  isFixedValueEnabled?: string | null;
  fixedValues?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ModelTemplateConditionInput = {
  _id?: ModelStringInput | null;
  _name?: ModelStringInput | null;
  _company?: ModelStringInput | null;
  _date?: ModelStringInput | null;
  _pib?: ModelStringInput | null;
  and?: Array<ModelTemplateConditionInput | null> | null;
  or?: Array<ModelTemplateConditionInput | null> | null;
  not?: ModelTemplateConditionInput | null;
};

export type GetTemplateQuery = {
  __typename: "Template";
  id: string;
  _id?: string | null;
  _name?: string | null;
  _description?: string | null;
  _document?: string | null;
  _company?: string | null;
  _date?: string | null;
  _invoice_date?: string | null;
  _due_date?: string | null;
  _price?: string | null;
  _vat?: string | null;
  _total?: string | null;
  _invoice_number?: string | null;
  _pib?: string | null;
  isFixedValueEnabled?: string | null;
  fixedValues?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ListTemplatesByClientIDQuery = {
  __typename: "ModelTemplateConnection";
  items: Array<{
    __typename: "Template";
    id: string;
    name: string;
    description?: string | null;
    clientID?: string | null;
    template: string;
    isFixedValueEnabled: string | null;
    fixedValues: string | null;
    createdAt: string;
    updatedAt: string;
  } | null>;
  nextToken?: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  constructor(
    public translate: TranslateService
  ) {
  }

  async ListTemplatesByClientID(
    clientID: string,
    sortDirection?: ModelSortDirection,
    filter?: ModelTemplateFilterInput,
    limit?: number,
    nextToken?: string
  ): Promise<ListTemplatesByClientIDQuery> {
    const statement = `query ListTemplatesByClientID($clientID: ID!, $sortDirection: ModelSortDirection, $filter: ModelTemplateFilterInput, $limit: Int, $nextToken: String) {
        listTemplatesByClientID(clientID: $clientID, sortDirection: $sortDirection, filter: $filter, limit: $limit, nextToken: $nextToken) {
          __typename
          items {
            __typename
            id
            name
            productsEnabled
            description
            clientID
            template
            isFixedValueEnabled
            fixedValues
            createdAt
            updatedAt
          }
          nextToken
        }
      }`;
    const gqlAPIServiceArguments: any = {
      clientID
    };
    if (sortDirection) {
      gqlAPIServiceArguments.sortDirection = sortDirection;
    }
    if (filter) {
      gqlAPIServiceArguments.filter = filter;
    }
    if (limit) {
      gqlAPIServiceArguments.limit = limit;
    }
    if (nextToken) {
      gqlAPIServiceArguments.nextToken = nextToken;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <ListTemplatesByClientIDQuery>response.data.listTemplatesByClientID;
  }

  async ListTemplatesByClientIDProductsEnabledPK(
    clientIDProductsEnabledPK: string,
    createdAt?: ModelStringKeyConditionInput,
    sortDirection?: ModelSortDirection,
    filter?: ModelTemplateFilterInput,
    limit?: number,
    nextToken?: string
  ): Promise<ListTemplatesByClientIDProductsEnabledPKQuery> {
    const statement = `query ListTemplatesByClientIDProductsEnabledPK($clientIDProductsEnabledPK: String!, $createdAt: ModelStringKeyConditionInput, $sortDirection: ModelSortDirection, $filter: ModelTemplateFilterInput, $limit: Int, $nextToken: String) {
        listTemplatesByClientIDProductsEnabledPK(
          clientIDProductsEnabledPK: $clientIDProductsEnabledPK
          createdAt: $createdAt
          sortDirection: $sortDirection
          filter: $filter
          limit: $limit
          nextToken: $nextToken
        ) {
          __typename
          items {
            __typename
            id
            name
            searchKeyword
            description
            clientID
            template
            productsEnabled
            isFixedValueEnabled
            fixedValues
            clientIDProductsEnabledPK
            createdAt
            updatedAt
          }
          nextToken
        }
      }`;
    const gqlAPIServiceArguments: any = {
      clientIDProductsEnabledPK
    };
    if (createdAt) {
      gqlAPIServiceArguments.createdAt = createdAt;
    }
    if (sortDirection) {
      gqlAPIServiceArguments.sortDirection = sortDirection;
    }
    if (filter) {
      gqlAPIServiceArguments.filter = filter;
    }
    if (limit) {
      gqlAPIServiceArguments.limit = limit;
    }
    if (nextToken) {
      gqlAPIServiceArguments.nextToken = nextToken;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <ListTemplatesByClientIDProductsEnabledPKQuery>(
      response.data.listTemplatesByClientIDProductsEnabledPK
    );
  }




  async ListTemplates(
    filter?: ModelTemplateFilterInput,
    limit?: number,
    nextToken?: string
  ): Promise<ListTemplatesQuery> {
    const statement = `query ListTemplates($filter: ModelTemplateFilterInput, $limit: Int, $nextToken: String) {
        listTemplates(filter: $filter, limit: $limit, nextToken: $nextToken) {
          __typename
          items {
            __typename
            id
            name
            productsEnabled
            description
            clientID
            template
            isFixedValueEnabled
            fixedValues
            createdAt
            updatedAt
          }
          nextToken
        }
      }`;
    const gqlAPIServiceArguments: any = {};
    if (filter) {
      gqlAPIServiceArguments.filter = filter;
    }
    if (limit) {
      gqlAPIServiceArguments.limit = limit;
    }
    if (nextToken) {
      gqlAPIServiceArguments.nextToken = nextToken;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <ListTemplatesQuery>response.data.listTemplates;
  }

  async GetTemplate(id: string): Promise<GetTemplateQuery> {
    const statement = `query GetTemplate($id: ID!) {
        getTemplate(id: $id) {
          __typename
          id
          name
          description
          productsEnabled
          template
          isFixedValueEnabled
          fixedValues
          createdAt
          updatedAt
        }
      }`;
    const gqlAPIServiceArguments: any = {
      id
    };
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <GetTemplateQuery>response.data.getTemplate;
  }

  async UpdateTemplate(
    input: UpdateTemplateInput,
    condition?: ModelTemplateConditionInput
  ): Promise<UpdateTemplateMutation> {
    const statement = `mutation UpdateTemplate($input: UpdateTemplateInput!, $condition: ModelTemplateConditionInput) {
        updateTemplate(input: $input, condition: $condition) {
          __typename
          id
          name
          productsEnabled
          description
          template
          isFixedValueEnabled
          fixedValues
          createdAt
          updatedAt
        }
      }`;
    const gqlAPIServiceArguments: any = {
      input
    };
    if (condition) {
      gqlAPIServiceArguments.condition = condition;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <UpdateTemplateMutation>response.data.updateTemplate;
  }

  async DeleteTemplate(
    input: DeleteTemplateInput,
    condition?: ModelTemplateConditionInput
  ): Promise<DeleteTemplateMutation> {
    const statement = `mutation DeleteTemplate($input: DeleteTemplateInput!, $condition: ModelTemplateConditionInput) {
        deleteTemplate(input: $input, condition: $condition) {
          id
        }
      }`;
    const gqlAPIServiceArguments: any = {
      input
    };
    if (condition) {
      gqlAPIServiceArguments.condition = condition;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <DeleteTemplateMutation>response.data.deleteTemplate;
  }

  async CreateTemplate(
    input: CreateTemplateInput,
    condition?: ModelTemplateConditionInput
  ): Promise<CreateTemplateMutation> {
    const statement = `mutation CreateTemplate($input: CreateTemplateInput!, $condition: ModelTemplateConditionInput) {
        createTemplate(input: $input, condition: $condition) {
          __typename
          id
          name
          productsEnabled
          description
          clientID
          template
          isFixedValueEnabled
          fixedValues
          createdAt
          updatedAt
        }
      }`;
    const gqlAPIServiceArguments: any = {
      input
    };
    if (condition) {
      gqlAPIServiceArguments.condition = condition;
    }
    const response = (await API.graphql(
      graphqlOperation(statement, gqlAPIServiceArguments)
    )) as any;
    return <CreateTemplateMutation>response.data.createTemplate;
  }

  isMandatoryKey(key: string) {
    return key === 'name'
      || key === 'description'
      || key === 'taxBase'
      || key === 'vat'
      || key === 'total'
      || key === 'code'
      || key === 'ID'
      || key === 'documentName'
      || key === 'companyName'
      || key === 'invoiceDate'
      || key === 'dueDate'
      || key === 'invoiceNumber'
      || key === 'vatID'
      || key === 'deductibleVAT'
      || key === 'nonDeductibleVAT'
      || key === 'deductibleTaxBase'
      || key === 'nonDeductibleTaxBase'
      || key === 'documentIndex'
      || key === 'documentURL'
      || key === 'productCode'
      || key === 'barcode'
      || key === 'number'
      || key === 'productName'
      || key === 'unit'
      || key === 'quantity'
      || key === 'priceWithTax'
      || key === 'priceWithoutTax'
      || key === 'discountPercentage'
      || key === 'discountAmount'
      || key === 'taxPercentage'
      || key === 'taxAmount'
      || key === 'retailPrice'
      || key === 'totalPrice'
      || key === 'totalPriceWithoutTax'
      || key === 'totalPriceWithoutTaxBeforeDiscount'
      || key === 'incomeExpenseDate'
      || key === 'viaBankAccount'
      || key === 'inGoodsMaterials'
      || key === 'grossEmployeeWages'
      || key === 'paidEntrepreneurContributions'
      || key === 'otherExpenses'
      || key === 'documentDescription'
      || key === 'konto'
      || key === 'schema'
      || key === 'buyerName'
      || key === 'buyerID'
      || key === 'buyerVATID'
      || isBankStatementField(key);
  }


  placeholderPicker(key: string) {
    let placeholderValue = '';
    switch (key) {
      case 'name':
        placeholderValue = 'Type Template name';
        break;
      case 'companyName':
        placeholderValue = this.translate.instant('Company');
        break;
      case 'dueDate':
        placeholderValue = this.translate.instant('Due Date');
        break;
      case 'description':
        placeholderValue = 'Add description';
        break;
      case 'documentName':
        placeholderValue = this.translate.instant('Document');
        break;
      case 'ID':
        placeholderValue = this.translate.instant('ID');
        break;
      case 'code':
        placeholderValue = this.translate.instant('Code');
        break;
      case 'productCode':
        placeholderValue = this.translate.instant('Product Code');
        break;
      case 'barcode':
        placeholderValue = this.translate.instant('Barcode');
        break;
      case 'number':
        placeholderValue = this.translate.instant('Number');
        break;
      case 'productName':
        placeholderValue = this.translate.instant('Product Name');
        break;
      case 'unit':
        placeholderValue = this.translate.instant('Unit');
        break;
      case 'quantity':
        placeholderValue = this.translate.instant('Quantity');
        break;
      case 'priceWithTax':
        placeholderValue = this.translate.instant('Price with Tax');
        break;
      case 'priceWithoutTax':
        placeholderValue = this.translate.instant('Price without Tax');
        break;
      case 'discountPercentage':
        placeholderValue = this.translate.instant('Discount %');
        break;
      case 'discountAmount':
        placeholderValue = this.translate.instant('Discount Amount');
        break;
      case 'taxPercentage':
        placeholderValue = this.translate.instant('Tax %');
        break;
      case 'taxAmount':
        placeholderValue = this.translate.instant('Tax Amount');
        break;
      case 'retailPrice':
        placeholderValue = this.translate.instant('Retail Price');
        break;
      case 'totalPrice':
        placeholderValue = this.translate.instant('Total Price');
        break;
      case 'totalPriceWithoutTax':
        placeholderValue = this.translate.instant('Total Price without Tax');
        break;
      case 'totalPriceWithoutTaxBeforeDiscount':
        placeholderValue = this.translate.instant('Total Price without Tax before Discount');
        break;
      case 'invoiceDate':
        placeholderValue = this.translate.instant('Invoice Date');
        break;
      case 'invoiceNumber':
        placeholderValue = this.translate.instant('Invoice Number');
        break;
      case 'taxBase':
        placeholderValue = this.translate.instant('Tax Base');
        break;
      case 'total':
        placeholderValue = this.translate.instant('Total');
        break;
      case 'vat':
        placeholderValue = this.translate.instant('VAT');
        break;
      case 'vatID':
        placeholderValue = this.translate.instant('VAT ID');
        break;
      case 'incomeExpenseDate':
        placeholderValue = this.translate.instant('Datum prihoda/rashoda');
        break;
      case 'viaBankAccount':
        placeholderValue = this.translate.instant('Preko bankovnog računa');
        break;
      case 'inGoodsMaterials':
        placeholderValue = this.translate.instant('U robi / materijalu');
        break;
      case 'grossEmployeeWages':
        placeholderValue = this.translate.instant('Bruto plaće zaposlenika');
        break;
      case 'paidEntrepreneurContributions':
        placeholderValue = this.translate.instant('Plaćeni doprinosi poduzetnika');
        break;
      case 'otherExpenses':
        placeholderValue = this.translate.instant('Ostalo');
        break;
      case 'documentDescription':
        placeholderValue = this.translate.instant('Opis dokumenta');
        break;
      case 'konto':
        placeholderValue = this.translate.instant('Konto');
        break;
      case 'schema':
        placeholderValue = this.translate.instant('Schema');
        break;
      case 'buyerName':
        placeholderValue = this.translate.instant('Buyer Name');
        break;
      case 'buyerID':
        placeholderValue = this.translate.instant('Buyer ID');
        break;
      case 'buyerVATID':
        placeholderValue = this.translate.instant('Buyer VAT ID');
        break;
      default:
        placeholderValue = BANK_STATEMENT_FIELD_LABELS[key] || '';
    }
    return placeholderValue;
  }


  setValue(key: string, value?: any) {
    switch (key) {
      case 'ID':
        return 'ID';
      case 'companyName':
        return 'Company';
      case 'dueDate':
        return 'Due Date';
      case 'invoiceDate':
        return 'Invoice Date';
      case 'invoiceNumber':
        return 'Invoice Number';
      case 'taxBase':
        return 'Tax Base';
      case 'documentName':
        return 'Document';
      case 'price':
        return 'Price';
      case 'total':
        return 'Total';
      case 'vat':
        return 'VAT';
      case 'vatID':
        return 'VAT ID';
      case 'code':
        return 'Code';
      case 'productCode':
        return 'Product Code';
      case 'barcode':
        return 'Barcode';
      case 'number':
        return 'Number';
      case 'productName':
        return 'Product Name';
      case 'unit':
        return 'Unit';
      case 'quantity':
        return 'Quantity';
      case 'priceWithTax':
        return 'Price with Tax';
      case 'priceWithoutTax':
        return 'Price without Tax';
      case 'discountPercentage':
        return 'Discount %';
      case 'discountAmount':
        return 'Discount Amount';
      case 'taxPercentage':
        return 'Tax %';
      case 'taxAmount':
        return 'Tax Amount';
      case 'retailPrice':
        return 'Retail Price';
      case 'totalPrice':
        return 'Total Price';
      case 'totalPriceWithoutTax':
        return 'Total Price without Tax';
      case 'totalPriceWithoutTaxBeforeDiscount':
        return 'Total Price without Tax before Discount';
      case 'incomeExpenseDate':
        return 'Datum prihoda/rashoda';
      case 'viaBankAccount':
        return 'Preko bankovnog računa';
      case 'inGoodsMaterials':
        return 'U robi / materijalu';
      case 'grossEmployeeWages':
        return 'Bruto plaće zaposlenika';
      case 'paidEntrepreneurContributions':
        return 'Plaćeni doprinosi poduzetnika';
      case 'otherExpenses':
        return 'Ostalo';
      case 'documentDescription':
        return 'Opis dokumenta';
      case 'konto':
        return 'Konto';
      case 'schema':
        return 'Schema';
      case 'buyerName':
        return 'Buyer Name';
      case 'buyerID':
        return 'Buyer ID';
      case 'buyerVATID':
        return 'Buyer VAT ID';
      default:
        return BANK_STATEMENT_FIELD_LABELS[key] || value;
    }
  }

}
